import React, { useState, useRef } from 'react';
import QuickColorPicker from './QuickColorPicker';

const LayerSymbologyIcon = ({ layer, map, layerStates, onOpenStylePanel, isStylePanelOpen }) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const iconRef = useRef(null);
  
  if (!layer || !map) return null;

  const getSymbologyIcon = () => {
    // If map isn't fully initialized with a style yet, fall back
    if (!map || typeof map.getLayer !== 'function' || !map.style) {
      return getDefaultSymbology();
    }
    // Check if this is a raster layer first
    if (layer.geometry_type === 'Raster') {
      return getRasterSymbology();
    }
    
    const layerId = `layer-${layer.id}`;
    
    // Try to get different layer variations (fill, stroke, main)
    let fillLayer, mainLayer;
    try {
      fillLayer = map.getLayer(`${layerId}-fill`);
      mainLayer = map.getLayer(layerId);
    } catch (_) {
      // If map isn't ready, return default symbology
      return getDefaultSymbology();
    }
    
    // Determine the actual layer to use and its type
    let mapLayer, layerType;
    
    if (fillLayer && fillLayer.type === 'fill') {
      mapLayer = fillLayer;
      layerType = 'fill';
    } else if (mainLayer) {
      mapLayer = mainLayer;
      layerType = mainLayer.type;
    } else {
      return getDefaultSymbology();
    }
    
    switch (layerType) {
      case 'circle':
        return getCircleSymbology(mapLayer);
      case 'line':
        return getLineSymbology(mapLayer);
      case 'fill':
        return getFillSymbology(mapLayer);
      case 'raster':
        return getRasterSymbology();
      default:
        return getDefaultSymbology();
    }
  };

  const getCircleSymbology = (mapLayer) => {
    const safe = (prop, def) => {
      try { return map.getPaintProperty(mapLayer.id, prop) ?? def; } catch (_) { return def; }
    };
    const color = safe('circle-color', '#007cbf');
    const radius = safe('circle-radius', 6);
    const strokeColor = safe('circle-stroke-color', '#ffffff');
    const strokeWidth = safe('circle-stroke-width', 1);

    return (
      <svg width="16" height="16" viewBox="0 0 16 16">
        <circle
          cx="8"
          cy="8"
          r={Math.min(radius, 6)}
          fill={color}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </svg>
    );
  };

  const getLineSymbology = (mapLayer) => {
    const safe = (prop, def) => {
      try { return map.getPaintProperty(mapLayer.id, prop) ?? def; } catch (_) { return def; }
    };
    const color = safe('line-color', '#007cbf');
    const width = safe('line-width', 3);

    return (
      <svg width="16" height="16" viewBox="0 0 16 16">
        <path
          d="M2 8 L14 8"
          stroke={color}
          strokeWidth={Math.min(width, 4)}
          fill="none"
          strokeLinecap="round"
        />
      </svg>
    );
  };

  const getFillSymbology = (mapLayer) => {
    const safeLayer = (id, prop, def) => {
      try { return map.getPaintProperty(id, prop) ?? def; } catch (_) { return def; }
    };
    const fillColor = safeLayer(mapLayer.id, 'fill-color', '#007cbf');
    const fillOpacity = safeLayer(mapLayer.id, 'fill-opacity', 0.5);
    
    // Get the base layer ID (remove -fill suffix)
    const baseLayerId = mapLayer.id.replace('-fill', '');
    const strokeLayerId = `${baseLayerId}-stroke`;
    
    let strokeColor = '#000000';
    let strokeWidth = 1;
    
    // Try to get stroke properties from the corresponding stroke layer
    let strokeLayer;
    try { strokeLayer = map.getLayer(strokeLayerId); } catch (_) { strokeLayer = null; }
    if (strokeLayer) {
      strokeColor = safeLayer(strokeLayerId, 'line-color', '#000000');
      strokeWidth = safeLayer(strokeLayerId, 'line-width', 1);
    }

    return (
      <svg width="16" height="16" viewBox="0 0 16 16">
        <rect
          x="2"
          y="2"
          width="12"
          height="12"
          fill={fillColor}
          fillOpacity={fillOpacity}
          stroke={strokeColor}
          strokeWidth={Math.min(strokeWidth, 2)}
        />
      </svg>
    );
  };

  const getRasterSymbology = () => {
    // Get raster data from layerStates if available
    const layerState = layerStates?.get(layer.id);
    const rasterData = layer.raster_data || layerState?.raster_data;
    
    // Check if we have raster metadata to determine band count
    const bands = rasterData?.bands || 1;
    const isRGB = bands >= 3;
    
    // Create unique gradient IDs to avoid conflicts
    const gradientId = `gradient-${layer.id}`;
    
    if (isRGB) {
      // Multi-band RGB raster - use actual image as background if available
      const preview = rasterData?.preview;
      
      if (preview) {
        // Use the actual raster preview image
        const imageData = `data:image/png;base64,${preview}`;
        return (
          <svg width="16" height="16" viewBox="0 0 16 16">
            <defs>
              <pattern id={`pattern-${layer.id}`} patternUnits="userSpaceOnUse" width="14" height="14">
                <image href={imageData} x="0" y="0" width="14" height="14" preserveAspectRatio="xMidYMid slice" />
              </pattern>
            </defs>
            <rect
              x="1"
              y="1"
              width="14"
              height="14"
              fill={`url(#pattern-${layer.id})`}
              stroke="#666666"
              strokeWidth="1"
              rx="1"
            />
          </svg>
        );
      } else {
        // Fallback to colorful gradient if no preview available
        return (
          <svg width="16" height="16" viewBox="0 0 16 16">
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#4CAF50" />
                <stop offset="33%" stopColor="#2196F3" />
                <stop offset="66%" stopColor="#FF9800" />
                <stop offset="100%" stopColor="#F44336" />
              </linearGradient>
            </defs>
            <rect
              x="1"
              y="1"
              width="14"
              height="14"
              fill={`url(#${gradientId})`}
              stroke="#666666"
              strokeWidth="1"
              rx="1"
            />
          </svg>
        );
      }
    } else {
      // Single-band raster - grayscale gradient for data visualization
      return (
        <svg width="16" height="16" viewBox="0 0 16 16">
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#1a1a1a" />
              <stop offset="25%" stopColor="#4a4a4a" />
              <stop offset="50%" stopColor="#808080" />
              <stop offset="75%" stopColor="#b0b0b0" />
              <stop offset="100%" stopColor="#e0e0e0" />
            </linearGradient>
          </defs>
          <rect
            x="1"
            y="1"
            width="14"
            height="14"
            fill={`url(#${gradientId})`}
            stroke="#666666"
            strokeWidth="1"
            rx="1"
          />
          {/* Data value indicator */}
          <g opacity="0.6">
            <rect x="11" y="2" width="3" height="1" fill="#ffffff" />
            <rect x="11" y="4" width="2" height="1" fill="#cccccc" />
            <rect x="11" y="6" width="1" height="1" fill="#888888" />
          </g>
          {/* Grid overlay for data structure */}
          <g stroke="#ffffff" strokeWidth="0.4" opacity="0.3">
            <line x1="4" y1="1" x2="4" y2="15" />
            <line x1="8" y1="1" x2="8" y2="15" />
            <line x1="12" y1="1" x2="12" y2="15" />
            <line x1="1" y1="4" x2="15" y2="4" />
            <line x1="1" y1="8" x2="15" y2="8" />
            <line x1="1" y1="12" x2="15" y2="12" />
          </g>
        </svg>
      );
    }
  };

  const getDefaultSymbology = () => {
    // Default symbology based on geometry type
    const geometryType = layer.geometry_type?.toLowerCase();
    
    switch (geometryType) {
      case 'point':
      case 'multipoint':
      case 'csv':
        return (
          <svg width="16" height="16" viewBox="0 0 16 16">
            <circle
              cx="8"
              cy="8"
              r="5"
              fill="#007cbf"
              stroke="#ffffff"
              strokeWidth="1"
            />
          </svg>
        );
      case 'linestring':
      case 'multilinestring':
        return (
          <svg width="16" height="16" viewBox="0 0 16 16">
            <path
              d="M2 8 L14 8"
              stroke="#007cbf"
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
            />
          </svg>
        );
      case 'polygon':
      case 'multipolygon':
        return (
          <svg width="16" height="16" viewBox="0 0 16 16">
            <rect
              x="2"
              y="2"
              width="12"
              height="12"
              fill="#007cbf"
              fillOpacity="0.5"
              stroke="#000000"
              strokeWidth="1"
            />
          </svg>
        );
      default:
        return (
          <svg width="16" height="16" viewBox="0 0 16 16">
            <rect
              x="2"
              y="2"
              width="12"
              height="12"
              fill="#cccccc"
              stroke="#666666"
              strokeWidth="1"
            />
          </svg>
        );
    }
  };

  const handleIconClick = (e) => {
    e.stopPropagation();
    // Skip color picker for raster layers
    if (layer.geometry_type === 'Raster') {
      if (onOpenStylePanel) {
        onOpenStylePanel(layer);
      }
      return;
    }
    
    // If style panel is open for this layer, don't open color picker
    if (isStylePanelOpen) {
      return;
    }
    
    setShowColorPicker(true);
  };

  const handleColorSelect = (color) => {
    if (!map) return;
    
    const layerId = `layer-${layer.id}`;
    const geometryType = (layer.geometry_type || layer.geometryType)?.toLowerCase();
    
    try {
      switch (geometryType) {
        case 'point':
        case 'multipoint':
          if (map.getLayer(layerId)) {
            map.setPaintProperty(layerId, 'circle-color', color);
          }
          break;
          
        case 'linestring':
        case 'multilinestring':
          if (map.getLayer(layerId)) {
            map.setPaintProperty(layerId, 'line-color', color);
          }
          break;
          
        case 'polygon':
        case 'multipolygon':
          const fillLayerId = `${layerId}-fill`;
          const strokeLayerId = `${layerId}-stroke`;
          if (map.getLayer(fillLayerId)) {
            map.setPaintProperty(fillLayerId, 'fill-color', color);
          }
          if (map.getLayer(strokeLayerId)) {
            map.setPaintProperty(strokeLayerId, 'line-color', color);
          }
          break;
      }
    } catch (error) {
      console.error('Error applying color:', error);
    }
    
    setShowColorPicker(false);
  };

  const handleOpenFullSettings = () => {
    setShowColorPicker(false);
    if (onOpenStylePanel) {
      onOpenStylePanel(layer);
    }
  };

  return (
    <>
      <div 
        ref={iconRef}
        className="layer-symbology-icon" 
        title={`Click to change color • ${layer.geometry_type} symbology`}
        style={{ cursor: 'pointer' }}
        onClick={handleIconClick}
      >
        {getSymbologyIcon()}
      </div>
      
      <QuickColorPicker
        isOpen={showColorPicker}
        onClose={() => setShowColorPicker(false)}
        onColorSelect={handleColorSelect}
        onOpenFullSettings={handleOpenFullSettings}
        anchorElement={iconRef.current}
      />
    </>
  );
};

export default LayerSymbologyIcon;
